import React, { Suspense, lazy, useEffect, useLayoutEffect, useRef } from 'react';
import { AnnouncementBar } from './components/AnnouncementBar';
import { Navbar } from './components/Navbar';
import { Footer } from './components/Footer';
import { Home } from './pages/Home';
import { useHashRoute, type Route } from './hooks/useHashRoute';
import { trackPageView } from './utils/tracking';

// Inner pages load on demand so the first visit only downloads the page being viewed.
const Pricing = lazy(() => import('./pages/Pricing').then(m => ({
  default: m.Pricing
})));
const AdditionalCosts = lazy(() => import('./pages/AdditionalCosts').then(m => ({
  default: m.AdditionalCosts
})));
const Contact = lazy(() => import('./pages/Contact').then(m => ({
  default: m.Contact
})));
const Industries = lazy(() => import('./pages/Industries').then(m => ({
  default: m.Industries
})));
const Crm = lazy(() => import('./pages/Crm').then(m => ({
  default: m.Crm
})));
const Websites = lazy(() => import('./pages/Websites').then(m => ({
  default: m.Websites
})));
const Gallery = lazy(() => import('./pages/Gallery').then(m => ({
  default: m.Gallery
})));

// Run non-essential work once the page has finished loading and the browser is idle.
function whenIdle(task: () => void) {
  const run = () => {
    const idle = (window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: {
        timeout: number;
      }) => number;
    }).requestIdleCallback;
    if (idle) idle(task, {
      timeout: 3000
    });else window.setTimeout(task, 1500);
  };
  if (document.readyState === 'complete') run();else window.addEventListener('load', run, {
    once: true
  });
}
const pageTitles: Record<Route, string> = {
  home: 'Ad ROI Marketing | Marketing & Conversions',
  pricing: 'Pricing & Plans | Ad ROI Marketing',
  additionalCosts: 'Additional Costs & Usage Rates | Ad ROI Marketing',
  contact: 'Contact Us | Ad ROI Marketing',
  industries: 'Industries We Serve | Ad ROI Marketing',
  crm: '1Dash.Pro CRM | Ad ROI Marketing',
  websites: 'Living Websites: Websites That Run Themselves | Ad ROI Marketing',
  gallery: 'Our Work: Website Gallery | Ad ROI Marketing'
};

// Per-page search descriptions. Written into the description and social tags on every page change.
const pageDescriptions: Record<Route, string> = {
  home: 'Ad ROI Marketing in Las Vegas: websites, marketing and the 1Dash.Pro CRM, Powered by HighLevel.',
  pricing: 'Simple monthly plans from $315: your website, phone system, reviews and the 1Dash.Pro CRM, Powered by HighLevel. See what each plan includes.',
  additionalCosts: 'Usage rates and add-ons for Ad ROI plans: calls, texts, email, extra websites and more. No surprises.',
  contact: 'Talk to a real person in Las Vegas about your website and marketing. Call (702) 213-4801 or send us a message.',
  industries: 'Websites, marketing and CRM built for home services, restaurants, med spas, real estate and more.',
  crm: 'The 1Dash.Pro CRM, Powered by HighLevel: contacts, calendar, texting, email, reviews and payments in one place.',
  websites: 'Change your website in minutes by phone, text, email, Slack or online. Built in 3 days, checked live before we say done.',
  gallery: 'Real websites and landing pages we built for real businesses. Every one started as a 3-day build.'
};
function setMeta(attr: 'name' | 'property', key: string, value: string) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', value);
}
export function App() {
  const route = useHashRoute();
  const firstView = useRef(true);
  useEffect(() => {
    // The chat widget is heavy, so it loads after the page content is ready.
    whenIdle(() => {
      if (document.getElementById('adroi-chat-widget')) return;
      const script = document.createElement('script');
      script.id = 'adroi-chat-widget';
      script.src = 'https://widgets.leadconnectorhq.com/loader.js';
      script.async = true;
      script.setAttribute('data-resources-url', 'https://widgets.leadconnectorhq.com/chat-widget/loader.js');
      script.setAttribute('data-widget-id', '6694c84d748b68476dace44d');
      document.body.appendChild(script);
    });
  }, []);
  useLayoutEffect(() => {
    document.title = pageTitles[route];
    setMeta('name', 'description', pageDescriptions[route]);
    setMeta('property', 'og:title', pageTitles[route]);
    setMeta('property', 'og:description', pageDescriptions[route]);
    // Initial load is tracked here; later hash changes are tracked by the global listener.
    if (firstView.current) {
      firstView.current = false;
      trackPageView();
    }
  }, [route]);
  return <div className="min-h-screen w-full bg-white font-sans text-gray-900 selection:bg-red-200 selection:text-red-900" data-id="element-0" data-mp="vvp3g24x264f">
      <AnnouncementBar data-id="element-1" data-mp="3klyffiq70vj9" />
      <Navbar route={route} data-id="element-2" data-mp="3gmpkwbnbwtmn" />
      <main data-id="element-3" data-mp="3glehwe1pjpwl">
        <Suspense fallback={<div className="min-h-screen" aria-busy="true" data-id="element-5" data-mp="3gl64ml1oaikv" />} data-id="element-4" data-mp="3globeblyof71">
          {route === 'pricing' && <Pricing data-id="element-6" data-mp="1w5zwo9s28ojc" />}
          {route === 'additionalCosts' && <AdditionalCosts data-id="element-7" data-mp="205qtov86k0wb" />}
          {route === 'contact' && <Contact data-id="element-8" data-mp="206a31njrlgyq" />}
          {route === 'industries' && <Industries data-id="element-9" data-mp="254lcvya27rgy" />}
          {route === 'crm' && <Crm data-id="element-10" data-mp="253jx5gi3mgsw" />}
          {route === 'websites' && <Websites data-id="element-11" data-mp="29vjv53elyzmu" />}
          {route === 'gallery' && <Gallery data-id="element-12" data-mp="29xegujxpevrz" />}
          {route === 'home' && <Home data-id="element-13" data-mp="2euwqydngjh1k" />}
        </Suspense>
      </main>
      <Footer data-id="element-14" data-mp="2eu4xb8df7tdx" />
    </div>;
}